// Maps a font key to its CSS font-family stack, for use as a theme's
// fontHeading/fontBody value. Each hosted family needs a matching @font-face in
// root.css; system fonts use the device's installed copy, so need no file.
// Tenant-licensed fonts aren't listed here: see client-fonts.config.js.
export const FONTS = {
  anton: "'Anton', sans-serif",
  archivo: "'Archivo', sans-serif",
  chivo: "'Chivo', sans-serif",
  epilogue: "'Epilogue', sans-serif",
  inter: "'Inter', sans-serif",
  'inter-tight': "'Inter Tight', sans-serif",
  'league-spartan': "'League Spartan', sans-serif",
  lora: "'Lora', serif",
  manrope: "'Manrope', sans-serif",
  montserrat: "'Montserrat', sans-serif",
  'noto-sans': "'Noto Sans', sans-serif",
  'open-sans': "'Open Sans', sans-serif",
  oswald: "'Oswald', sans-serif",
  outfit: "'Outfit', sans-serif",
  roboto: "'Roboto', sans-serif",
  sora: "'Sora', sans-serif",
  'source-sans-3': "'Source Sans 3', sans-serif",
  'space-grotesk': "'Space Grotesk', sans-serif",
  urbanist: "'Urbanist', sans-serif",
  'barlow-regular': "'Barlow Regular', sans-serif",
  'barlow-bold': "'Barlow Bold', sans-serif",
  'poppins-regular': "'Poppins Regular', sans-serif",
  'poppins-bold': "'Poppins Bold', sans-serif",

  // System fonts: not on every device, so each falls back to a similar font
  arial: 'Arial, Helvetica, sans-serif',
  helvetica: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  tahoma: 'Tahoma, Verdana, Geneva, sans-serif',
};

// Maps a font role to the CSS custom property the embed reads it from.
const FONT_CSS_PROPERTIES = {
  heading: '--font-heading',
  body: '--font-body',
};

// Font-family stacks for the current tenant's licensed fonts, filled in by
// loadTenantFonts
const tenantFonts = {};

// Resolved now, while this script is the one executing, so tenant font folders
// sit next to the bundle (dist/fonts/) wherever the page itself is served
const FONTS_BASE_URL = new URL(
  'fonts/',
  document.currentScript?.src || window.location.href,
);

/**
 * Normalises a configured font value to a font key.
 *
 * @param {string} value font key (e.g. 'open-sans') or family name (e.g. 'Open Sans')
 * @returns {string} font key
 */
const toFontKey = (value) =>
  String(value).trim().toLowerCase().replace(/\s+/g, '-');

/**
 * Gets the folder a tenant's licensed fonts are published to. Must match
 * fontFolderId in webpack.config.js.
 *
 * @param {string} tenant tenant subdomain
 * @returns {Promise<string>} folder id
 */
const getFontFolderId = (tenant) => {
  let bytes = new TextEncoder().encode(
    process.env.FONT_FOLDER_NAMESPACE + tenant,
  );
  return crypto.subtle.digest('SHA-256', bytes).then((hash) =>
    Array.from(new Uint8Array(hash))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16),
  );
};

/**
 * Gets the font-family stack for a configured font value.
 *
 * @param {string} value font key (e.g. 'open-sans') or family name (e.g. 'Open Sans')
 * @returns {string|undefined} font-family stack, or undefined if the font isn't supported
 */
export const getFontFamily = (value) => {
  if (!value) {
    return undefined;
  }
  let key = toFontKey(value);
  return FONTS[value] || FONTS[key] || tenantFonts[key];
};

/**
 * Registers the tenant's licensed fonts, so getFontFamily/applyFonts can use
 * them. Only fetches the tenant's manifest when a configured font isn't a
 * shared one, and only the requested fonts' files are added. A tenant can
 * only load its own fonts: another tenant's key isn't in its manifest.
 * Internal (pulselive) tenants load every tenant's fonts, matching the
 * settings microfrontend, which lets them select any of them.
 *
 * @param {string} tenant tenant subdomain
 * @param {string[]} values configured font values (heading, body)
 * @returns {Promise<void>} resolves once registered; never rejects, so
 *   unavailable fonts fall back like any unsupported font
 */
export const loadTenantFonts = (tenant, values = []) => {
  let keys = values
    .filter(Boolean)
    .filter((value) => !getFontFamily(value))
    .map(toFontKey);
  if (!keys.length) {
    return Promise.resolve();
  }

  let fontTenant = tenant.includes('pulselive')
    ? process.env.INTERNAL_FONT_TENANT
    : tenant;

  let folderUrl;
  return getFontFolderId(fontTenant)
    .then((folderId) => {
      folderUrl = new URL(`${folderId}/`, FONTS_BASE_URL);
      return fetch(new URL('fonts.json', folderUrl));
    })
    .then((res) => (res.ok ? res.json() : {}))
    .then((manifest) => {
      keys.forEach((key) => {
        let font = manifest[key];
        if (!font) {
          return;
        }
        font.faces.forEach((face) => {
          let url = new URL(face.file, folderUrl);
          document.fonts.add(
            new FontFace(font.family, `url('${url}') format('woff2')`, {
              weight: face.weight,
              style: face.style || 'normal',
              display: 'swap',
            }),
          );
        });
        tenantFonts[key] = `'${font.family}', sans-serif`;
      });
    })
    .catch(() => {});
};

/**
 * Sets the heading/body fonts as inline custom properties on the given
 * element. Unsupported or missing values are skipped, so the element keeps
 * whatever font it inherits.
 *
 * @param {HTMLElement} el element to style
 * @param {{ heading?: string, body?: string }} fonts configured font per role
 */
export const applyFonts = (el, fonts = {}) => {
  Object.entries(FONT_CSS_PROPERTIES).forEach(([role, cssProperty]) => {
    let fontFamily = getFontFamily(fonts[role]);
    if (fontFamily) {
      el.style.setProperty(cssProperty, fontFamily);
    }
  });
};
